import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRight, ChevronDown, ChevronRight, Loader2, Search, Undo2 } from 'lucide-react';
import { TablePagination } from '../table/SortableHeader';
import { lmError, PAGE_SIZE_OPTIONS } from './lmApi';
import { CopyId, FOCUS_RING, GhostButton, LmTip, Pill, StateMessage } from './LmBits';
import { BATCH_HELP, BATCH_STATUSES, BATCH_STATUS_META, JOB_HELP, MATCH_LABELS, OPERATION_HELP, OPERATION_STATUS, TAG_HELP, batchStatusOf, formatDate } from './lmColumns';
import LmBulkDialog from './LmBulkDialog';

// Tone classes (not the shared Pill's inline colours): each carries its Graphite pair.
const MIGRATION_TAG = 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300';
const NEUTRAL_TAG = 'bg-muted text-ink-2';

/** The shared Pill's shape, with theme-aware tone classes in place of inline colours.
 *  With a description it is a Tab stop (D1), like Pill. */
function Tag({ children, tone, description, testId }) {
    return (
        <LmTip description={description}>
            <span tabIndex={description ? 0 : undefined} data-testid={testId}
                  className={`inline-flex items-center h-[22px] px-2 rounded-md text-[11.5px] font-semibold whitespace-nowrap ${tone}${description ? ` ${FOCUS_RING}` : ''}`}>
                {children}
            </span>
        </LmTip>
    );
}
const ROLLBACK_FROM = new Set(['committed', 'partially_committed', 'partially_restored']);

/**
 * The one-time Contact Owner migration (#862): the parent operation, its parts
 * and its approval. A different admin with `contacts:migrate_owner` approves or
 * rejects here (the server refuses the requester regardless); Roll back
 * restores every part in reverse, keeping contacts changed since as conflicts.
 * Rolling back (the whole migration or one part) also needs
 * `contacts:restore_owner` (`canRestoreOwner`), as the server does.
 */
export function MigrationPanel({ api, operationId, canMigrateOwners, canRevert, canRestoreOwner = false, currentUserId,
                                 onChanged, onClose }) {
    const [state, setState] = useState({ loading: true });
    const [dialog, setDialog] = useState(null);
    const load = useCallback(() => {
        api.ownerMigration(operationId, { staleOwner: true })
            .then((op) => setState({ loading: false, op }))
            .catch((err) => setState({ loading: false, error: lmError(err, 'Could not load this migration.') }));
    }, [api, operationId]);
    useEffect(() => { load(); }, [load]);
    if (state.loading) return <div className="p-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>;
    if (state.error) return <div className="px-4 py-3 text-[12.5px] text-red-800 dark:text-bad" role="alert">{state.error}</div>;
    const { op } = state;
    const requester = op.requested_by || {};
    const pending = op.status === 'pending_approval';
    const mayDecide = pending && canMigrateOwners && requester.id && requester.id !== currentUserId;
    const parts = op.parts || [];
    const done = parts.filter((p) => p.status === 'committed').length;
    // Rows the parts hold once staged; unknown (no count line) before any part is.
    const partRows = parts.reduce((n, p) => n + (Number(p.rows) || 0), 0) || undefined;
    const refresh = () => { load(); onChanged?.(); };
    const decisions = {
        approve: ['Approve this migration?', 'Approve migration', (reason) => api.approveOperation(op.operation_id, reason), 'Migration approved — its parts start now'],
        reject: ['Reject this migration?', 'Reject migration', (reason) => api.rejectOperation(op.operation_id, reason), 'Migration rejected'],
        rollback: ['Roll back the whole migration?', 'Roll back migration', (reason) => api.rollbackMigration(op.operation_id, reason), 'Rollback started'],
    };
    const active = dialog && decisions[dialog];
    return (
        <div className="px-4 py-3 bg-muted/50 border border-border rounded-xl" data-testid="lm-migration-panel">
            <div className="flex items-center gap-2 flex-wrap">
                <Tag tone={MIGRATION_TAG} description={TAG_HELP.owner_migration}>Owner migration</Tag>
                <LmTip description={OPERATION_HELP[op.status] || `Migration state: ${String(op.status).replace(/_/g, ' ')}.`}>
                    <span tabIndex={0} data-testid="lm-migration-status"
                          className={`text-[13px] font-semibold text-foreground ${FOCUS_RING}`}>
                        {OPERATION_STATUS[op.status] || op.status}
                    </span>
                </LmTip>
                <span className="text-[12.5px] text-ink-2 tabular-nums">{done} of {parts.length} parts committed</span>
                <span className="flex-1" />
                {onClose && <GhostButton className="h-[28px]" onClick={onClose}>Close</GhostButton>}
            </div>
            <div className="mt-1 text-[12.5px] text-ink-2">
                Requested by {requester.name || '—'}{op.reason ? ` · ${op.reason}` : ''}
                {op.approval ? ` · ${op.approval.decision === 'approve' ? 'approved' : 'rejected'} by ${op.approval.approver_name}${op.approval.reason ? ` (${op.approval.reason})` : ''}` : ''}
            </div>
            {op.stale_owner > 0 && (
                <div className="mt-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-warn-bg text-[12.5px] text-amber-800 dark:text-warn" data-testid="lm-migration-stale">
                    {op.stale_owner} contact{op.stale_owner === 1 ? '' : 's'} changed owner since staging; they will be kept as conflicts.
                </div>
            )}
            <ul className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-1.5 text-[12.5px]" data-testid="lm-migration-parts">
                {parts.map((p) => (
                    <li key={p.part_index} className="flex justify-between bg-card border border-border rounded-lg px-3 py-1.5">
                        <span className="text-muted-foreground">Part {p.part_index}</span>
                        <span className="font-semibold">{String(p.status || 'pending').replace(/_/g, ' ')}</span>
                    </li>
                ))}
            </ul>
            <div className="mt-2 flex flex-wrap items-center gap-2">
                <CopyId label="Migration ID" value={op.operation_id} testId="lm-migration-id" />
                <span className="flex-1" />
                {pending && !mayDecide && (
                    <span className="text-[12.5px] text-muted-foreground" data-testid="lm-migration-awaiting">
                        {requester.id === currentUserId ? 'A different admin must approve your request.' : 'Waiting for an admin with migration rights.'}
                    </span>
                )}
                {mayDecide && (
                    <>
                        <GhostButton className="h-[30px]" testId="lm-migration-reject" onClick={() => setDialog('reject')}>Reject</GhostButton>
                        <GhostButton className="h-[30px]" testId="lm-migration-approve" onClick={() => setDialog('approve')}>Approve</GhostButton>
                    </>
                )}
                {canMigrateOwners && canRevert && canRestoreOwner && (op.restorable ?? ROLLBACK_FROM.has(op.status)) && (
                    <GhostButton className="h-[30px]" icon={Undo2} testId="lm-migration-rollback" onClick={() => setDialog('rollback')}>Roll back</GhostButton>
                )}
            </div>
            <LmBulkDialog
                open={!!active}
                onClose={() => setDialog(null)}
                title={active?.[0]}
                description={dialog === 'rollback'
                    ? 'Restores the previous owner of every contact this migration moved, newest part first. Contacts whose owner changed since are kept and reported as conflicts.'
                    : 'The requester is told of your decision. A reason is required.'}
                confirmLabel={active?.[1]}
                tone="dark"
                askReason
                requireReason
                reasonPlaceholder="Reason (kept in the audit trail)"
                reasonMaxLength={500}
                selection={null}
                count={partRows}
                run={async (_sel, { reason }) => {
                    await active[2](reason);
                    toast.success(active[3]);
                    refresh();
                    return { succeeded: 1, failed: 0, results: [] };
                }}
            />
        </div>
    );
}

/**
 * Import History (#775 LM-07): one row per batch with the prototype's columns
 * (Batch, File, Records, Committed, Skipped, Imported By, Date, Status), and
 * an expandable lifecycle: counts by stage, entity, match, validation and
 * outcome, links to the batch's rows in each view, commit jobs with their
 * outcomes, Retry of failed rows, Cancel, Admin-only journaled Revert, and the
 * audit trail. The batch status comes from the server (the #771 precedence),
 * so the badge and the status filter agree.
 */
const VALIDATION_LABELS = { valid: 'Ready', needs_review: 'Needs review', invalid: 'Invalid', none: 'Not validated' };
const ENTITY_LABELS = { Company: 'Companies', Contact: 'Contacts', none: 'Unresolved' };

function CountGroup({ title, counts, labels, testId }) {
    const entries = Object.entries(counts || {}).filter(([, n]) => n > 0);
    return (
        <div className="bg-card border border-border rounded-lg px-3 py-2" data-testid={testId}>
            <div className="text-[11px] font-semibold tracking-[0.05em] text-muted-foreground">{title}</div>
            {entries.length ? (
                <ul className="mt-1 space-y-0.5 text-[12.5px] text-foreground">
                    {entries.map(([k, n]) => (
                        <li key={k} className="flex justify-between gap-3">
                            <span>{labels[k] || k.replace(/_/g, ' ')}</span><span className="font-semibold tabular-nums">{n}</span>
                        </li>
                    ))}
                </ul>
            ) : <div className="mt-1 text-[12.5px] text-muted-foreground">None</div>}
        </div>
    );
}

/** One line per batch stage, for the collapsed row (#775 QA suggestion). */
function Stages({ counts: c = {} }) {
    const parts = [['Imports', c.imported], ['Staging', c.staging], ['Pooling', c.pool], ['Committed', c.committed]];
    return (
        <span className="text-[12px] text-ink-2 whitespace-nowrap tabular-nums" data-testid="lm-history-stages">
            {parts.map(([l, n], i) => (
                <span key={l} className={n ? 'text-foreground' : 'text-muted-foreground'}>{i ? ' · ' : ''}{l} {n || 0}</span>
            ))}
        </span>
    );
}
export async function waitForRevert(api, jobId, { intervalMs = 1500, timeoutMs = 30 * 60 * 1000 } = {}) {
    const deadline = Date.now() + timeoutMs;
    let misses = 0;
    while (Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, intervalMs));
        let job;
        try {
            // eslint-disable-next-line no-await-in-loop
            job = (await api.job(jobId, { page_size: 1 }))?.job;
            misses = 0;
        } catch (err) {
            misses += 1;  // a transient 5xx must not abandon a revert that is still running
            if (misses >= 5) throw err;
            continue;
        }
        if (job && job.status !== 'reverting') {
            if (job.status === 'reverted' || job.status === 'reverted_with_conflicts') return job.revert?.result;
            throw new Error('The revert stopped part-way. What was undone stays undone — run Revert again to finish it.');
        }
    }
    throw new Error('The revert is still running. Check Import History in a few minutes.');
}

function JobRow({ job, api, canCommit, canRevert, canMigrateOwners, canRestoreOwner, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [confirmRevert, setConfirmRevert] = useState(false);
    const c = job.counts || {};
    const act = async (fn, ok) => {
        setBusy(true);
        try { await fn(); toast.success(ok); onChanged?.(); } catch (err) { toast.error(lmError(err, 'That action failed.')); } finally { setBusy(false); }
    };
    const migrationPart = job.mode === 'owner_migration';
    const revertable = ['completed', 'partial', 'cancelled', 'failed', 'interrupted'].includes(job.status) && (c.committed || 0) > 0
        && (!migrationPart || (canMigrateOwners && canRestoreOwner));
    return (
        <li className="px-3 py-2.5 border-t border-border/60 first:border-t-0" data-testid="lm-history-job">
            <div className="flex items-center gap-3 flex-wrap">
                <Tag tone={NEUTRAL_TAG} testId="lm-history-job-status"
                     description={JOB_HELP[job.status] || `Job state: ${String(job.status).replace(/_/g, ' ')}.`}>{job.status.replace(/_/g, ' ')}</Tag>
                {migrationPart && <Tag tone={MIGRATION_TAG} description={TAG_HELP.migration_part}>{`Migration part ${job.part_index ?? ''}`.trim()}</Tag>}
                <span className="text-[12.5px] text-ink-2 tabular-nums">
                    {c.committed ?? 0} committed · {(c.retryable_error || 0) + (c.permanent_error || 0)} failed
                    {c.returned_to_staging ? ` · ${c.returned_to_staging} back to staging` : ''}
                    {c.reverted ? ` · ${c.reverted} reverted` : ''}
                </span>
                <span className="text-xs text-muted-foreground">{formatDate(job.created_at, true)} · {job.created_by_name || '—'}</span>
                <span className="flex-1" />
                {canCommit && (c.retryable_error || 0) > 0 && !['running', 'queued', 'interrupted'].includes(job.status) && (
                    <GhostButton className="h-[30px]" disabled={busy} onClick={() => act(() => api.retryJob(job.job_id), 'Retrying failed rows')}>Retry failed</GhostButton>
                )}
                {canCommit && job.status === 'interrupted' && (
                    <GhostButton className="h-[30px]" disabled={busy} testId="lm-resume-job"
                                 title="The job stopped part-way (e.g. a deploy). Resume the rows it did not finish."
                                 onClick={() => act(() => api.retryJob(job.job_id), 'Resuming the commit')}>Resume</GhostButton>
                )}
                {canCommit && ['running', 'queued'].includes(job.status) && (
                    <GhostButton className="h-[30px]" disabled={busy} onClick={() => act(() => api.cancelJob(job.job_id), 'Cancel requested')}>Cancel</GhostButton>
                )}
                {canRevert && revertable && (
                    <GhostButton className="h-[30px]" disabled={busy} onClick={() => setConfirmRevert(true)} testId="lm-revert">Revert</GhostButton>
                )}
            </div>
            {(job.drift || []).length > 0 && (
                <div className="mt-1 text-xs text-muted-foreground">Preview vs outcome: {job.drift.map((d) => `${d.count} ${d.reason.replace(/_/g, ' ')}`).join(' · ')}</div>
            )}
            {job.revert?.result && (
                <div className="mt-1 text-xs text-muted-foreground">
                    Revert: {job.revert.result.reverted_records} records, {job.revert.result.reverted_fields} fields,{' '}
                    {job.revert.result.reverted_relationships} relationships reverted · {job.revert.result.conflicts.length} kept because they changed after commit
                </div>
            )}
            <LmBulkDialog
                open={confirmRevert}
                onClose={() => setConfirmRevert(false)}
                title={migrationPart ? 'Roll back this migration part?' : 'Revert this commit?'}
                description={migrationPart
                    ? 'Restores the previous owner of every contact this part moved. Contacts whose owner changed since are kept and reported as conflicts.'
                    : 'Reverts only what this commit wrote. Records or fields edited after the commit, manually created relationships, and companies other records now use are kept and reported as conflicts.'}
                confirmLabel={migrationPart ? 'Roll back part' : 'Revert commit'}
                tone="dark"
                askReason={migrationPart}
                requireReason={migrationPart}
                reasonMaxLength={500}
                reasonPlaceholder="Reason (kept in the audit trail)"
                selection={null}
                count={c.committed || 0}
                run={async (_sel, { reason } = {}) => {
                    const res = await api.revertJob(job.job_id, migrationPart ? reason : undefined);
                    let result = res.result;
                    if (res.started) {
                        // A large revert finishes in the background: follow the job.
                        result = await waitForRevert(api, job.job_id);
                    }
                    toast.success(result?.conflicts?.length ? `Reverted with ${result.conflicts.length} conflict(s) kept` : 'Commit reverted');
                    onChanged?.();
                    return { succeeded: 1, failed: 0, results: [] };
                }}
            />
        </li>
    );
}

function Detail({ api, importId, canCommit, canRevert, canDelete, canMigrateOwners, canRestoreOwner, currentUserId, onChanged,
                  onOpenRows }) {
    const [state, setState] = useState({ loading: true });
    const [confirmDelete, setConfirmDelete] = useState(false);
    const load = useCallback(() => {
        api.importDetail(importId).then((d) => setState({ loading: false, ...d }))
            .catch((err) => setState({ loading: false, error: lmError(err, 'Could not load this import.') }));
    }, [api, importId]);
    useEffect(() => { load(); }, [load]);
    if (state.loading) return <div className="p-4"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>;
    if (state.error) return <div className="p-4 text-[12.5px] text-red-800 dark:text-bad" role="alert">{state.error}</div>;
    const imp = state.import;
    const c = imp.counts || {};
    const refresh = () => { load(); onChanged?.(); };
    return (
        <div className="px-4 py-3 bg-muted/50 border-t border-border" data-testid="lm-history-detail">
            {imp.mode === 'owner_migration' && imp.parent_operation_id && (
                <div className="mb-3">
                    <MigrationPanel api={api} operationId={imp.parent_operation_id} canMigrateOwners={canMigrateOwners}
                                    canRevert={canRevert} canRestoreOwner={canRestoreOwner} currentUserId={currentUserId}
                                    onChanged={refresh} />
                </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[12.5px] text-foreground">
                {[['In Imports', c.imported], ['In Staging', c.staging], ['In Pooling', c.pool], ['Committed', c.committed],
                  ['Needs retry', c.errors], ['Skipped', c.skipped], ['Reverted', c.reverted], ['Deleted', c.deleted]].map(([l, v]) => (
                    <div key={l} className="flex justify-between bg-card border border-border rounded-lg px-3 py-1.5">
                        <span className="text-muted-foreground">{l}</span><span className="font-semibold tabular-nums">{v || 0}</span>
                    </div>
                ))}
            </div>
            {state.breakdown && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2" data-testid="lm-history-breakdown">
                    <CountGroup title="BY ENTITY" counts={state.breakdown.entity} labels={ENTITY_LABELS} testId="lm-breakdown-entity" />
                    <CountGroup title="BY MATCH" counts={state.breakdown.match} labels={{ ...MATCH_LABELS, none: 'Not matched' }} testId="lm-breakdown-match" />
                    <CountGroup title="BY VALIDATION" counts={state.breakdown.validation} labels={VALIDATION_LABELS} testId="lm-breakdown-validation" />
                    <CountGroup title="COMMIT OUTCOMES" counts={state.breakdown.outcome}
                                labels={{ committed: 'Committed', failed: 'Failed', retry: 'Needs retry', committing: 'Committing', reverted: 'Reverted', skipped: 'Skipped' }}
                                testId="lm-breakdown-outcome" />
                </div>
            )}
            {onOpenRows && imp.status === 'available' && (c.imported || c.staging || c.pool || c.errors) > 0 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="lm-history-links">
                    {[['imports', 'Imports', c.imported], ['staging', 'Staging Area', c.staging], ['pool', 'For Pooling', (c.pool || 0) + (c.errors || 0)]]
                        .filter(([, , n]) => n > 0)
                        .map(([tab, label, n]) => (
                            <GhostButton key={tab} icon={ArrowRight} className="h-[30px]" testId={`lm-history-open-${tab}`}
                                         onClick={() => onOpenRows(tab, imp.import_id)}>
                                Open {n} in {label}
                            </GhostButton>
                        ))}
                </div>
            )}
            <div className="mt-2">
                <CopyId label="Import ID" value={imp.import_id} testId="lm-history-import-id" />
            </div>
            <div className="mt-1 text-[11.5px] text-muted-foreground break-all">
                {imp.source_type} · snapshot {imp.source_checksum} · mapping {imp.mapping_version} · {imp.transform_version || 'legacy'}
            </div>
            <div className="mt-3 text-[12px] font-semibold tracking-[0.05em] text-muted-foreground">COMMIT JOBS</div>
            {state.jobs.length ? (
                <ul className="mt-1 bg-card border border-border rounded-[10px]">
                    {state.jobs.map((j) => <JobRow key={j.job_id} job={j} api={api} canCommit={canCommit} canRevert={canRevert}
                                                   canMigrateOwners={canMigrateOwners} canRestoreOwner={canRestoreOwner}
                                                   onChanged={refresh} />)}
                </ul>
            ) : <div className="mt-1 text-[12.5px] text-muted-foreground">No commits yet.</div>}
            <div className="mt-3 text-[12px] font-semibold tracking-[0.05em] text-muted-foreground">RECENT ACTIVITY</div>
            <ol className="mt-1 space-y-1">
                {(state.events || []).slice(0, 12).map((e) => (
                    <li key={e.event_id} className="text-xs text-ink-2">
                        <span className="text-muted-foreground tabular-nums">{formatDate(e.created_at, true)}</span> · {e.action.replace(/_/g, ' ')}
                        {e.actor_name ? ` · ${e.actor_name}` : ''}{e.reason ? ` · ${e.reason}` : ''}
                    </li>
                ))}
            </ol>
            {canDelete && imp.status === 'available' && (
                <div className="mt-3">
                    <GhostButton className="h-[30px]" onClick={() => setConfirmDelete(true)}>Delete import</GhostButton>
                </div>
            )}
            {canDelete && imp.status === 'deleted' && (
                <div className="mt-3">
                    <GhostButton className="h-[30px]" onClick={async () => {
                        try { const r = await api.restoreImport(imp.import_id); toast.success(`Restored ${r.rows_restored} rows`); refresh(); } catch (err) { toast.error(lmError(err)); }
                    }}>Restore import</GhostButton>
                </div>
            )}
            <LmBulkDialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                title="Delete this import?"
                description="Rows not yet committed move to Deleted (restorable). Committed CRM records are never deleted — use Revert for those."
                confirmLabel="Delete import"
                tone="dark"
                askReason
                count={(c.total || 0) - (c.committed || 0)}
                run={async (_sel, { reason }) => {
                    const r = await api.deleteImport(imp.import_id, reason);
                    toast.success(`Import deleted · ${r.rows_deleted} rows moved to Deleted`);
                    refresh();
                    return { succeeded: 1, failed: 0, results: [] };
                }}
            />
        </div>
    );
}

export default function LmHistoryView({ api, refreshKey, canCommit, canRevert, canDelete, onChanged, query, onQueryChange, onOpenRows,
                                       canMigrateOwners = false, canRestoreOwner = false, currentUserId }) {
    // Search, page and page size live in the page's URL state like every other
    // view (#770 QA: a reload used to drop them). Local state is only the
    // fallback when the view is rendered without the page shell.
    const [local, setLocal] = useState({ q: '', page: 1, size: PAGE_SIZE_OPTIONS[0], bstatus: '' });
    const { q, page, size } = query || local;
    const bstatus = (query || local).bstatus || '';
    const setQuery = (patch, { resetPage = true } = {}) => {
        const next = resetPage && !('page' in patch) ? { ...patch, page: 1 } : patch;
        if (onQueryChange) onQueryChange(next, { resetPage: false });
        else setLocal((s) => ({ ...s, ...next }));
    };
    const [state, setState] = useState({ loading: true, data: [], total: 0 });
    const [open, setOpen] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        let alive = true;
        setState((s) => ({ ...s, loading: true }));
        const t = setTimeout(() => {
            // Deleted imports stay listed: Restore lives on their row.
            api.imports({ page, page_size: size, search: q || undefined, include_deleted: true, batch_status: bstatus || undefined })
                .then((res) => alive && setState({ loading: false, data: res.data, total: res.total }))
                .catch((err) => alive && setState({ loading: false, data: [], total: 0, error: lmError(err, 'Could not load import history.') }));
        }, 250);
        return () => { alive = false; clearTimeout(t); };
    }, [api, page, size, q, bstatus, refreshKey, tick]);

    const changed = () => { setTick((t) => t + 1); onChanged?.(); };
    const totalPages = Math.max(1, Math.ceil(state.total / size));
    // A bell link (`?tab=history&migration=<id>`) opens that migration's panel.
    const migrationId = query?.migration || '';
    return (
        <div className="flex-1 min-h-[420px] flex flex-col bg-card border border-border rounded-xl overflow-hidden" data-testid="lm-history">
            {migrationId && (
                <div className="px-4 pt-3.5">
                    <MigrationPanel api={api} operationId={migrationId} canMigrateOwners={canMigrateOwners} canRevert={canRevert}
                                    canRestoreOwner={canRestoreOwner} currentUserId={currentUserId} onChanged={changed}
                                    onClose={() => setQuery({ migration: '' }, { resetPage: false })} />
                </div>
            )}
            <div className="flex items-center gap-3 px-4 py-3.5 flex-wrap">
                <label className="flex items-center gap-2 w-[360px] max-w-full h-[38px] px-3 border border-input rounded-[9px] bg-card">
                    <Search className="w-[15px] h-[15px] text-muted-foreground" aria-hidden="true" />
                    <input value={q} onChange={(e) => setQuery({ q: e.target.value })} placeholder="Search imports..."
                           aria-label="Search imports" className="flex-1 min-w-0 border-0 outline-none bg-transparent text-[13px] text-foreground placeholder:text-muted-foreground" />
                </label>
                <label className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-2">
                    <span>Status</span>
                    <select value={bstatus} onChange={(e) => setQuery({ bstatus: e.target.value })} aria-label="Filter by batch status"
                            data-testid="lm-history-status-filter"
                            className="h-[30px] px-2 border border-input rounded-lg bg-card text-[12.5px] text-foreground">
                        <option value="">All</option>
                        {BATCH_STATUSES.map((k) => <option key={k} value={k}>{BATCH_STATUS_META[k][0]}</option>)}
                    </select>
                </label>
                <span className="flex-1" />
                <span className="text-[13px] text-muted-foreground tabular-nums">{state.total} imports</span>
            </div>
            <div className="flex-1 min-h-0 overflow-auto border-t border-border" data-testid="lm-history-scroll">
                <table className="border-collapse w-full min-w-[1000px]">
                    <thead>
                        <tr className="bg-muted">
                            {['', 'Batch', 'File', 'Records', 'Stages', 'Committed', 'Skipped', 'Imported By', 'Date', 'Status'].map((h, i) => (
                                <th key={i} scope="col" className="sticky top-0 z-10 bg-muted shadow-[inset_0_-1px_0_hsl(var(--border))] px-3.5 py-2.5 text-left text-[11.5px] font-semibold text-ink-2 whitespace-nowrap">{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {state.data.map((imp) => {
                            const isOpen = open === imp.import_id;
                            return (
                                <React.Fragment key={imp.import_id}>
                                    <tr className="border-b border-border/60 cursor-pointer hover:bg-muted/50" onClick={() => setOpen(isOpen ? null : imp.import_id)}>
                                        <td className="pl-4 py-[11px] w-8">
                                            <button type="button" aria-expanded={isOpen} aria-label={`${isOpen ? 'Hide' : 'Show'} details for ${imp.batch_name}`}
                                                    onClick={(e) => { e.stopPropagation(); setOpen(isOpen ? null : imp.import_id); }}>
                                                {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                                            </button>
                                        </td>
                                        <td className="px-3.5 py-[11px] text-[13px] font-medium text-foreground whitespace-nowrap">
                                            {imp.batch_name || '—'}
                                            {imp.mode === 'owner_migration' && (
                                                <span className="ml-2 align-middle" data-testid="lm-history-migration-badge">
                                                    <Tag tone={MIGRATION_TAG} description={TAG_HELP.owner_migration}>{`Owner migration${imp.part_index ? ` · part ${imp.part_index}` : ''}`}</Tag>
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3.5 py-[11px] text-[13px] text-foreground whitespace-nowrap">{imp.file_name || '—'}</td>
                                        <td className="px-3.5 py-[11px] text-[13px] text-foreground tabular-nums">{imp.counts?.total ?? imp.row_count ?? 0}</td>
                                        <td className="px-3.5 py-[11px]"><Stages counts={imp.counts} /></td>
                                        <td className="px-3.5 py-[11px] text-[13px] text-foreground tabular-nums">{imp.counts?.committed ?? 0}</td>
                                        <td className="px-3.5 py-[11px] text-[13px] text-foreground tabular-nums">{imp.counts?.skipped ?? 0}</td>
                                        <td className="px-3.5 py-[11px] text-[13px] text-foreground whitespace-nowrap">{imp.created_by_name || '—'}</td>
                                        <td className="px-3.5 py-[11px] text-[13px] text-foreground whitespace-nowrap tabular-nums">{formatDate(imp.confirmed_at || imp.created_at, true)}</td>
                                        <td className="px-3.5 py-[11px]"><Pill label={batchStatusOf(imp)} description={BATCH_HELP[imp.batch_status] ?? BATCH_HELP.imported}
                                                                                  testId="lm-history-batch-status" /></td>
                                    </tr>
                                    {isOpen && (
                                        <tr><td colSpan={10} className="p-0">
                                            <Detail api={api} importId={imp.import_id} canCommit={canCommit} canRevert={canRevert} canDelete={canDelete}
                                                    canMigrateOwners={canMigrateOwners} canRestoreOwner={canRestoreOwner}
                                                    currentUserId={currentUserId} onChanged={changed} onOpenRows={onOpenRows} />
                                        </td></tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
                {state.error && <StateMessage kind="error" title="Could not load import history" detail={state.error} />}
                {!state.loading && !state.error && state.data.length === 0 && <StateMessage kind="empty" title="No import records found" />}
                {state.loading && !state.data.length && <div className="py-12 text-center text-[13px] text-muted-foreground" role="status">Loading…</div>}
            </div>
            <div className="shrink-0">
                <TablePagination page={page} totalPages={totalPages} total={state.total} pageSize={size} alwaysShow
                                 onPageChange={(p) => setQuery({ page: p }, { resetPage: false })}
                                 onPageSizeChange={(n) => setQuery({ size: n })}
                                 pageSizeOptions={PAGE_SIZE_OPTIONS} disabled={state.loading} />
            </div>
        </div>
    );
}


/**
 * Deleted imports, for the Deleted view (#775 QA: an import's tombstone must be
 * visible in Deleted, not only in Import History). Each one restores here.
 */
export function DeletedImports({ api, refreshKey, search, canRestore, onRestored }) {
    const [state, setState] = useState({ loading: true, data: [], total: 0 });
    const [tick, setTick] = useState(0);
    useEffect(() => {
        let alive = true;
        api.imports({ page: 1, page_size: 25, batch_status: 'deleted', include_deleted: true, search: search || undefined })
            .then((res) => alive && setState({ loading: false, data: res.data, total: res.total }))
            .catch((err) => alive && setState({ loading: false, data: [], total: 0, error: lmError(err, 'Could not load deleted imports.') }));
        return () => { alive = false; };
    }, [api, refreshKey, search, tick]);
    if (state.loading || (!state.error && state.total === 0)) return null;
    const restore = async (imp) => {
        try {
            const r = await api.restoreImport(imp.import_id);
            toast.success(`Restored ${imp.batch_name || 'the import'} · ${r.rows_restored} rows`);
            setTick((t) => t + 1);
            onRestored?.();
        } catch (err) { toast.error(lmError(err)); }
    };
    return (
        <section className="shrink-0 mb-3.5 bg-card border border-border rounded-xl overflow-hidden" data-testid="lm-deleted-imports"
                 aria-labelledby="lm-deleted-imports-title">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
                <h2 id="lm-deleted-imports-title" className="text-[13px] font-semibold text-foreground">Deleted imports</h2>
                <span className="text-[12px] text-muted-foreground tabular-nums">{state.total}</span>
                <span className="text-[12px] text-muted-foreground">· their rows are listed below, marked "With import"</span>
            </div>
            {state.error ? <p role="alert" className="px-4 py-2 text-[12.5px] text-red-800 dark:text-bad">{state.error}</p> : (
                <div className="max-h-[200px] overflow-auto">
                    <table className="border-collapse w-full min-w-[760px] text-[12.5px]">
                        <thead>
                            <tr>{['Batch', 'File', 'Rows', 'Reason', 'Deleted By', 'Deleted On', 'Purges On', ''].map((h, i) => (
                                <th key={i} scope="col" className="sticky top-0 bg-muted shadow-[inset_0_-1px_0_hsl(var(--border))] px-3.5 py-2 text-left text-[11.5px] font-semibold text-ink-2 whitespace-nowrap">{h}</th>
                            ))}</tr>
                        </thead>
                        <tbody>
                            {state.data.map((imp) => (
                                <tr key={imp.import_id} className="border-b border-border/60" data-testid="lm-deleted-import">
                                    <td className="px-3.5 py-2 font-medium text-foreground whitespace-nowrap">{imp.batch_name || '—'}</td>
                                    <td className="px-3.5 py-2 text-foreground whitespace-nowrap">{imp.file_name || '—'}</td>
                                    <td className="px-3.5 py-2 tabular-nums">{imp.counts?.deleted ?? 0}</td>
                                    <td className="px-3.5 py-2 text-foreground min-w-[200px]">{imp.deleted?.reason || '—'}</td>
                                    <td className="px-3.5 py-2 whitespace-nowrap">{imp.deleted?.by_name || '—'}</td>
                                    <td className="px-3.5 py-2 whitespace-nowrap tabular-nums">{formatDate(imp.deleted?.at)}</td>
                                    <td className="px-3.5 py-2 whitespace-nowrap tabular-nums">{imp.purge_at ? formatDate(imp.purge_at) : 'Kept'}</td>
                                    <td className="px-3.5 py-2 text-right">
                                        {canRestore && (
                                            <GhostButton icon={Undo2} className="h-[28px]" testId="lm-restore-import" onClick={() => restore(imp)}>
                                                Restore import
                                            </GhostButton>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}
